import { useQuery, UseQueryResult } from 'react-query'
import useApi from 'hooks/useApi'
import { getUserInfoById } from 'dataSource/users/routes'
import { getPatientsforSupportUser } from 'dataSource/appointment/routes'

export type PatientDetailsList = PatientDetails[]

export type UserResult = UseQueryResult<User, Error>
export type PatientDetailsResult = UseQueryResult<PatientDetailsList, Error>

export const useUserDetailsQuery = (
  userId: string,
  { enabled = true }: { enabled?: boolean } = {}
): [UserResult, PatientDetailsResult] => {
  const { request } = useApi()

  const getUser = async () => await request.get(getUserInfoById(userId))

  const getPatients = async (): Promise<PatientDetailsList> => {
    const response = await request.get(getPatientsforSupportUser(userId))

    // The support patients endpoint returns 422 for ghost RPs (placeholder
    // accounts used to park patient records). use-http resolves that error
    // body as the response value rather than rejecting, so without this guard
    // `data` becomes a non-array `{ statusCode, message, error }` object.
    // Consumers then call .forEach/.find/.filter on it and crash the whole
    // page. Throwing here keeps `data` as `PatientDetailsList | undefined` and
    // surfaces the failure through react-query's error state instead.
    if (!Array.isArray(response)) {
      throw new Error(
        (response as { message?: string } | null)?.message ??
          'Failed to load patients for this user'
      )
    }

    return response
  }

  const user = useQuery<User, Error>({
    queryKey: ['userDetails', userId],
    queryFn: getUser,
    refetchOnWindowFocus: false,
    // Don't fire with an empty id — lets shared surfaces (e.g. the nav bar)
    // call this only when a user is actually in context.
    enabled: !!userId,
  })

  const patients = useQuery<PatientDetailsList, Error>({
    queryKey: ['patientsBelongingToUser', userId],
    queryFn: getPatients,
    refetchOnWindowFocus: false,
    // Never fan out patient loads for a ghost RP. This gate lives here, keyed
    // off the user query's authoritative `isGhostRp`, so every caller of this
    // hook is covered — not just the ones that remember to pass enabled:false.
    // (UserInfoSection and RelatedPatientsSection both call this hook ungated
    // and share this query's cache by key, so a page-level gate alone leaks.)
    // Waiting on `!user.isLoading` matters: before the user query settles,
    // `!user.data?.isGhostRp` is `!undefined` and would let one request
    // through. `isLoading` (not `isSuccess`) so a failed user fetch still
    // falls back to today's behaviour of loading patients.
    // Callers may still pass enabled:false to defer for their own reasons.
    enabled: !!userId && enabled && !user.isLoading && !user.data?.isGhostRp,
  })

  return [user, patients]
}
